import { useSnackbar } from "notistack";
import { AuditsAPI } from "@/api/index.js";
import { Audit } from "@/types";
import { useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import LoggingAPI from "@/api/logging";
import { Button, Modal, TextInput } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

interface DeleteAuditModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (defaultInfo?: Audit) => void;
  onCancel: () => void;
  buttonProps?: {
    cancelText: string;
    submitText: string;
  };
  defaultInfo: Audit;
}

export const DeleteAuditModal: React.FC<DeleteAuditModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
  buttonProps,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmation, setConfirmation] = useState("");
  const [confirmationError, setConfirmationError] = useState("");
  const handleSubmit = async () => {
    if (confirmation !== defaultInfo?.name) {
      enqueueSnackbar("Please type the audit name to confirm deletion", {
        variant: "error",
      });
      setConfirmationError("Please type the audit name to confirm deletion");
      return;
    }
    setSubmitting(true);
    try {
      await AuditsAPI.delete({
        uuid: defaultInfo.uuid,
      });

      enqueueSnackbar("Audit has been deleted", {
        variant: "success",
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Deleted Audit: ${defaultInfo?.name}`,
        event: "audit:delete",
        object_type: "audit",
        object_uuid: defaultInfo?.uuid,
      });

      onSubmit?.(defaultInfo);
    } catch (error) {
      console.error("Error deleting audit:", error);
      enqueueSnackbar(`Error deleting audit.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <Modal open={show} onClose={onClose} style={{ width: "400px" }}>
      <Modal.Title>Delete Audit</Modal.Title>
      <div style={{ fontWeight: 500, marginBottom: 10 }}>
        <p>
          Are you sure you want to delete this audit? This action cannot be
          undone.
        </p>
        <p>
          <span style={{ fontWeight: 600, fontSize: 16 }}>
            "{defaultInfo?.name}"
          </span>
        </p>
        {confirmation !== defaultInfo?.name && (
          <p style={{ color: "grey" }}>
            Please type the audit name to confirm deletion
          </p>
        )}
        <TextInput
          label="Confirmation"
          placeholder={defaultInfo?.name}
          value={confirmation}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setConfirmation(e.target.value)
          }
          error={confirmationError}
        />
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Delete Audit"}
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
